import { Component, computed, inject, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";

import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { GateCatalogService } from "../../../../../services/api/gate-catalog/gate-catalog.service";
import { first, forkJoin, map, Observable } from "rxjs";

import { AutoCompleteModule } from "@openng/optimus-ui/autocomplete";
import { ControllerCatalogService } from "../../../../../services/api/controller-catalog/controller-catalog.service";
import { MessageService } from "@openng/optimus-ui/api";
import { BotevoCoresService } from "../../../../../services/api/botevo-cores/botevo-cores.service";
import { CONTROLLER_CATALOG_ITEM_TYPES } from "../../../../../enums/controllerCatalogItemTypes";

import { AssetService } from "../../../../../services/api/asset/asset.service";
import { ASSET_TYPES } from "../../../../../enums/assetTypes.enum";
import { NgStyle } from "@angular/common";

import { InputNumberModule } from "@openng/optimus-ui/inputnumber";
import { CacheService } from "../../../../../services/cache/cache.service";
import { environment } from "../../../../../../environments/environment";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import { PermissionService } from "../../../../../services/api/permission/permission.service";
import { HasPermissionDirective } from "../../../../../shared/directives/has-permission/has-permission.directive";
import { ControllerCatalogItem } from "../../../../../models/api/fault/controllerCatalogItem";
import { ControllerManufacturerObject } from "../../../../../models/api/fault/controllerManufacturer";
import { ControllerModelObject } from "../../../../../models/api/fault/controllerModel";
import { Core } from "../../../../../models/api/tenant";
import { GateSystem, GateSystemDTO } from "../../../../../models/api/asset/gateSystem";
import { Reference } from "../../../../../models/shared/refs";
import { IMAGE_PATTERN, PDF_PATTERN } from "../../../../../constants/regExp";
import { DatePickerModule } from "@openng/optimus-ui/datepicker";
import { SelectChangeEvent, SelectModule } from "@openng/optimus-ui/select";
import { RenderParameters } from "pdfjs-dist/types/src/display/api";
import { isNotNullOrUndefined } from "../../../../../shared/utils/utils";

@Component({
  standalone: true,
  selector: "app-gate",
  imports: [
    ButtonComponent,
    ReactiveFormsModule,
    TranslateModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    DatePickerModule,
    AutoCompleteModule,
    NgStyle,
    ProgressSpinnerModule,
    HasPermissionDirective,
  ],
  templateUrl: "./gate.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./gate.component.scss",
})
export class GateComponent implements OnInit {
  public translate = inject(TranslateService);
  public permissions = inject(PermissionService);
  options = {
    yesNo: [
      { label: this.translate.instant("common.yes"), value: true },
      { label: this.translate.instant("common.no"), value: false },
    ],
    noneStandardThermo: [
      { label: this.translate.instant("common.none"), value: "none" },
      { label: this.translate.instant("common.standard"), value: "standard" },
      { label: this.translate.instant("common.thermo"), value: "thermo" },
    ],
  };
  public gateTypes!: Reference[];
  public allControllerManufacturers: WritableSignal<ControllerManufacturerObject[]> = signal([]);
  public allControllerModels: WritableSignal<ControllerModelObject[]> = signal([]);
  public allCores: Core[] = [];
  public loading = signal(false);
  public saveButtonLoading = signal(false);
  public floorMapSource!: string;
  public point: WritableSignal<{ x: number; y: number }> = signal({
    x: 0,
    y: 0,
  });
  public requestStarted = signal(false);
  private config = inject(DynamicDialogConfig);
  public gateSystem: GateSystem = this.config.data.asset;
  private ref = inject(DynamicDialogRef);
  private gateCatalog = inject(GateCatalogService);
  private controllerCatalog = inject(ControllerCatalogService);
  private message = inject(MessageService);
  private coreService = inject(BotevoCoresService);
  private assetService = inject(AssetService);
  private cache = inject(CacheService);
  private actionType!: "add" | "edit";
  private imgWidth = signal(0);
  private imgHeight = signal(0);
  public getPointCoordinates = computed(() => {
    if (this.point()) {
      return {
        "top.px": this.point().y * this.imgHeight(),
        "left.px": this.point().x * this.imgWidth(),
      };
    } else {
      return {};
    }
  });
  private hasNoMasterDataPermissions = !this.permissions.hasPermission([
    this.permissions.allPermissions.ASSET_MASTER_DATA_CREATE,
    this.permissions.allPermissions.ASSET_MASTER_DATA_UPDATE,
  ]);
  private hasNoDynamicDataPermissions = !this.permissions.hasPermission([
    this.permissions.allPermissions.ASSET_DYNAMIC_DATA_CREATE,
    this.permissions.allPermissions.ASSET_DYNAMIC_DATA_UPDATE,
  ]);
  public form = new FormGroup({
    label: new FormControl(
      {
        value: this.gateSystem.label,
        disabled: this.hasNoDynamicDataPermissions,
      },
      Validators.required
    ),
    core: new FormControl(
      {
        value: this.gateSystem.core,
        disabled: this.hasNoMasterDataPermissions,
      },
      Validators.required
    ),
    coreId: new FormControl(
      {
        value: this.gateSystem.coreId,
        disabled: this.hasNoMasterDataPermissions,
      },
      this.gateSystem.core ? Validators.required : Validators.nullValidator
    ),
    personalDoor: new FormControl(
      {
        value:
          this.gateSystem.personalDoor !== undefined && this.gateSystem.personalDoor !== null
            ? this.gateSystem.personalDoor
            : false,
        disabled: this.hasNoMasterDataPermissions,
      },
      Validators.required
    ),
    gateHistoryCycle: new FormControl(
      {
        value: this.gateSystem.gateHistoryCycle,
        disabled: this.hasNoMasterDataPermissions,
      },
      this.gateSystem.core ? Validators.required : Validators.nullValidator
    ),
    activeDate: new FormControl(
      {
        value: isNotNullOrUndefined(this.gateSystem.activeDate) ? new Date(this.gateSystem.activeDate) : null,
        disabled: this.hasNoMasterDataPermissions,
      },
      Validators.required
    ),
    tempOffset: new FormControl({
      value: this.gateSystem.tempOffset,
      disabled: this.hasNoMasterDataPermissions,
    }),
    width: new FormControl(
      {
        value: this.gateSystem.width,
        disabled: this.hasNoMasterDataPermissions,
      },
      Validators.required
    ),
    height: new FormControl(
      {
        value: this.gateSystem.height,
        disabled: this.hasNoMasterDataPermissions,
      },
      Validators.required
    ),
    retailerNumber: new FormControl({
      value: this.gateSystem.retailerNumber,
      disabled: this.hasNoMasterDataPermissions,
    }),
    gateNumber: new FormControl({
      value: this.gateSystem.gateNumber,
      disabled: this.hasNoMasterDataPermissions,
    }),
    normalOperationThreshold: new FormControl({
      value: this.gateSystem.normalOperationThreshold,
      disabled: this.hasNoDynamicDataPermissions,
    }),
    hasButler: new FormControl({
      value: this.gateSystem.hasButler,
      disabled: this.hasNoMasterDataPermissions,
    }),
    hasGuard: new FormControl(
      {
        value: this.gateSystem.hasGuard,
        disabled: this.hasNoMasterDataPermissions,
      },
      this.gateSystem.core ? Validators.required : Validators.nullValidator
    ),
    loadingBridge: new FormControl(
      {
        value: this.gateSystem.loadingBridge,
        disabled: this.hasNoMasterDataPermissions,
      },
      this.gateSystem.core ? Validators.required : Validators.nullValidator
    ),
    quickupGate: new FormControl(
      {
        value: this.gateSystem.quickupGate,
        disabled: this.hasNoMasterDataPermissions,
      },
      this.gateSystem.core ? Validators.required : Validators.nullValidator
    ),
    isSluiceGate: new FormControl(
      {
        value: this.gateSystem.isSluiceGate,
        disabled: this.hasNoMasterDataPermissions,
      },
      this.gateSystem.core ? Validators.required : Validators.nullValidator
    ),
    emailEnergyLoss: new FormControl({
      value: this.gateSystem.emailEnergyLoss,
      disabled: this.hasNoDynamicDataPermissions,
    }),
    gateType: new FormControl({
      value: this.gateSystem.gateType?.id,
      disabled: this.hasNoMasterDataPermissions,
    }),
    expectedEnergyLossPerYear: new FormControl({
      value: this.gateSystem.expectedEnergyLossPerYear,
      disabled: this.hasNoDynamicDataPermissions,
    }),
    gateManufacturer: new FormControl({
      value: this.gateSystem.gateManufacturer,
      disabled: this.hasNoMasterDataPermissions,
    }),
    gateMake: new FormControl({
      value: this.gateSystem.gateMake,
      disabled: this.hasNoMasterDataPermissions,
    }),
    controlManufacturer: new FormControl(
      {
        value: this.gateSystem.controlManufacturer,
        disabled: this.hasNoMasterDataPermissions,
      },
      this.gateSystem.core ? Validators.required : Validators.nullValidator
    ),
    controlModel: new FormControl(
      {
        value: this.gateSystem.controlModel,
        disabled: this.hasNoMasterDataPermissions,
      },
      this.gateSystem.core ? Validators.required : Validators.nullValidator
    ),
    position: new FormGroup({
      latitude: new FormControl(this.gateSystem.position?.latitude),
      longitude: new FormControl(this.gateSystem.position?.longitude),
    }),
  });

  public ngOnInit() {
    const subjects: {
      cores?: Observable<Core[] | null>;
      gateTypes?: Observable<Reference[]>;
      controllerManufacturers?: Observable<ControllerManufacturerObject[]>;
    } = {};
    if (
      this.permissions.hasPermission(this.permissions.allPermissions.ASSET_MASTER_DATA_READ) ||
      this.permissions.hasPermission(this.permissions.allPermissions.ASSET_DYNAMIC_DATA_READ)
    ) {
      subjects.gateTypes = this.getGateTypesOptions();
      subjects.controllerManufacturers = this.getControllerManufacturers();
    }
    if (this.permissions.hasPermission(this.permissions.allPermissions.CORE_READ)) {
      subjects.cores = this.getCores();
    }
    this.loading.set(true);

    forkJoin(subjects).subscribe({
      next: (res: {
        cores?: Core[] | null;
        gateTypes?: Reference[];
        controllerManufacturers?: ControllerManufacturerObject[];
      }) => {
        if (res.gateTypes) this.gateTypes = res.gateTypes;
        if (this.config.data.asset) {
          this.gateSystem = this.config.data.asset;
          this.extractFloorMapSource(this.gateSystem);
        }
        if (res.controllerManufacturers) {
          this.allControllerManufacturers.set(res.controllerManufacturers);
        }
        if (res.cores) {
          this.allCores = res.cores.filter((core: Core) => {
            return !core.assigned || core.id === this.gateSystem?.coreId;
          });
        }
        if (this.config.data.actionType) {
          this.actionType = this.config.data.actionType;
        }

        if (this.gateSystem.position && this.gateSystem.position.latitude && this.gateSystem.position.longitude) {
          this.point.set({
            x: this.gateSystem.position.latitude,
            y: this.gateSystem.position.longitude,
          });
        }
        if (this.gateSystem?.controlManufacturer?.id) {
          this.onControllerManufacturerSelected({
            originalEvent: new Event("change"),
            value: this.gateSystem.controlManufacturer,
          });
        }

        this.loading.set(false);
      },
    });
  }

  /**
   * Called when the image is loaded.
   * @param event The event that triggered the image load.
   */
  onImageLoad(event: Event) {
    const img = event.target as HTMLImageElement;
    this.imgWidth.set(img.width);
    this.imgHeight.set(img.height);
  }

  /**
   * Called when the user clicks on the floor map image.
   * Calculates the relative coordinates of the click on the image and updates the form with the new position.
   * @param event The MouseEvent that triggered the function call.
   */
  public onImageClick(event: MouseEvent): void {
    const rect = (event.target as HTMLElement).getBoundingClientRect();

    const imageWidth = rect.width;
    const imageHeight = rect.height;

    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const xRelative = x / imageWidth;
    const yRelative = y / imageHeight;

    this.point.set({ x: xRelative, y: yRelative });
    this.form?.patchValue({
      position: { latitude: xRelative, longitude: yRelative },
    });
  }

  /**
   * Called when the user selects a value from the core dropdown.
   * Toggles the required validators for the core, gate history cycle, control manufacturer, control model, has guard, is sluice gate, quickup gate, and loading bridge form controls based on the selected value.
   * @param event The SelectChangeEvent that triggered the function call.
   */
  public onCoreChange(event: SelectChangeEvent) {
    const hasCore = event.value;

    if (hasCore) {
      this.form.controls["coreId"].setValidators(Validators.required);
      this.form.controls["gateHistoryCycle"].setValidators(Validators.required);
      this.form.controls["controlManufacturer"].setValidators(Validators.required);
      this.form.controls["controlModel"].setValidators(Validators.required);
      this.form.controls["hasGuard"].setValidators(Validators.required);
      this.form.controls["isSluiceGate"].setValidators(Validators.required);
      this.form.controls["quickupGate"].setValidators(Validators.required);
      this.form.controls["loadingBridge"].setValidators(Validators.required);
    } else {
      this.form.controls["coreId"].clearValidators();
      this.form.controls["gateHistoryCycle"].clearValidators();
      this.form.controls["controlManufacturer"].clearValidators();
      this.form.controls["controlModel"].clearValidators();
      this.form.controls["hasGuard"].clearValidators();
      this.form.controls["isSluiceGate"].clearValidators();
      this.form.controls["quickupGate"].clearValidators();
      this.form.controls["loadingBridge"].clearValidators();
    }
    this.form.controls["coreId"].updateValueAndValidity();
    this.form.controls["gateHistoryCycle"].updateValueAndValidity();
    this.form.controls["controlManufacturer"].updateValueAndValidity();
    this.form.controls["controlModel"].updateValueAndValidity();
    this.form.controls["hasGuard"].updateValueAndValidity();
    this.form.controls["isSluiceGate"].updateValueAndValidity();
    this.form.controls["quickupGate"].updateValueAndValidity();
    this.form.controls["loadingBridge"].updateValueAndValidity();
  }

  /**
   * Updates the available controller models based on the selected manufacturer.
   * Fetches models from the controller catalog and updates the form control with the models.
   * If the current selected model is not available, it clears the control model field.
   * @param event - The change event from the dropdown selection, containing the selected manufacturer.
   */
  public onControllerManufacturerSelected(event: SelectChangeEvent) {
    const selectedParent: Reference = event.value as Reference;
    this.controllerCatalog
      .getCatalogItems(CONTROLLER_CATALOG_ITEM_TYPES.MODEL, selectedParent.id)
      .pipe(first())
      .subscribe((controllerModels) => {
        if (controllerModels) {
          this.allControllerModels.set(controllerModels as ControllerModelObject[]);
          const index = this.allControllerModels().findIndex(
            (controllerModel) => controllerModel.id === this.gateSystem.controlModel?.id
          );
          if (index === -1) {
            this.form.patchValue({ controlModel: null });
          }
        }
      });
  }

  /**
   * Validates the form and constructs a gate system DTO.
   * If the form is valid and a request is not already in progress, assigns the DTO to the gate system properties and initiates the save operation.
   * If the form is invalid, displays an error message.
   */
  public save() {
    const gateSystemDTO = this.getGateSystemDTO();

    if (gateSystemDTO && this.form.valid) {
      if (!this.requestStarted()) {
        this.gateSystem.properties = gateSystemDTO;
        this.saveGateSystem();
      }
    } else {
      this.message.add({
        key: "global",
        severity: "error",
        summary: this.translate.instant("common.error"),
        detail: this.translate.instant("common.invalid_form"),
        life: 4500,
      });
    }
  }

  /**
   * Returns a GateSystemDTO object based on the form's values.
   * If the form is invalid or the label is empty, return null
   * If the user does not have the permission to update or create, removes the activeDate and gateHistoryCycle fields.
   * If the core is not set, removes the coreId, gateHistoryCycle, controlManufacturer, controlModel, hasButler, hasGuard, emailEnergyLoss, expectedEnergyLossPerDay, tempOffset, loadingBridge, quickupGate, and isSluiceGate fields.
   * @returns GateSystemDTO or null on invalid form
   */
  getGateSystemDTO(): GateSystemDTO | null {
    const gateSystemFormValues = this.form?.value;
    if (gateSystemFormValues && gateSystemFormValues.label) {
      const gateSystemDTO: GateSystemDTO = {
        label: gateSystemFormValues.label,
        core: gateSystemFormValues.core,
        coreId: gateSystemFormValues.coreId,
        gateHistoryCycle: gateSystemFormValues.gateHistoryCycle,
        personalDoor: gateSystemFormValues.personalDoor,
        controlManufacturer: gateSystemFormValues.controlManufacturer,
        controlModel: gateSystemFormValues.controlModel,
        hasButler: gateSystemFormValues.hasButler,
        hasGuard: gateSystemFormValues.hasGuard,
        emailEnergyLoss: gateSystemFormValues.emailEnergyLoss,
        gateManufacturer: gateSystemFormValues.gateManufacturer,
        gateMake: gateSystemFormValues.gateMake,
        expectedEnergyLossPerYear: gateSystemFormValues.expectedEnergyLossPerYear,
        tempOffset: gateSystemFormValues.tempOffset?.toString(),
        loadingBridge: gateSystemFormValues.loadingBridge,
        quickupGate: gateSystemFormValues.quickupGate,
        isSluiceGate: gateSystemFormValues.isSluiceGate,
        normalOperationThreshold: gateSystemFormValues.normalOperationThreshold,
        width: gateSystemFormValues.width,
        height: gateSystemFormValues.height,
        retailerNumber: gateSystemFormValues.retailerNumber,
        gateNumber: gateSystemFormValues.gateNumber,
      };
      if (gateSystemFormValues.position?.latitude && gateSystemFormValues.position?.longitude) {
        gateSystemDTO.position = gateSystemFormValues.position;
      }
      if (gateSystemFormValues.gateType !== undefined && gateSystemFormValues.gateType !== null) {
        gateSystemDTO.gateType = { id: gateSystemFormValues.gateType };
      }
      if (gateSystemFormValues.activeDate) {
        gateSystemDTO.activeDate = gateSystemFormValues.activeDate.getTime();
      }

      if (
        this.permissions.missesPermission(this.permissions.allPermissions.ASSET_MASTER_DATA_UPDATE) &&
        this.permissions.missesPermission(this.permissions.allPermissions.ASSET_MASTER_DATA_CREATE)
      ) {
        delete gateSystemDTO.activeDate;
        delete gateSystemDTO.gateHistoryCycle;
      }

      if (!gateSystemDTO.core) {
        delete gateSystemDTO.coreId;
        delete gateSystemDTO.gateHistoryCycle;
        delete gateSystemDTO.controlManufacturer;
        delete gateSystemDTO.controlModel;
        delete gateSystemDTO.hasButler;
        delete gateSystemDTO.hasGuard;
        delete gateSystemDTO.emailEnergyLoss;
        delete gateSystemDTO.expectedEnergyLossPerYear;
        delete gateSystemDTO.tempOffset;
        delete gateSystemDTO.loadingBridge;
        delete gateSystemDTO.quickupGate;
        delete gateSystemDTO.isSluiceGate;
      }
      return gateSystemDTO;
    } else {
      return null;
    }
  }

  /**
   * Closes the dialog without returning any value.
   */
  public closeDialog() {
    this.ref.close();
  }

  /**
   * @param actionType 'edit' if the gate system was edited, otherwise `undefined`
   * @param gateSystemId the id of the gate system that was edited
   * - `next`: called on success, removes the gate system from cache if actionType is 'edit' and closes the dialog
   * - `error`: called on failure, shows an error message and removes the gate system from cache if actionType is 'edit'
   */
  private afterServerCommunication(actionType?: string, gateSystemId?: string) {
    return {
      next: () => {
        this.requestStarted.set(false);
        if (actionType === "edit") {
          this.cache.delete(environment.baseUrl + `/asset/${gateSystemId}?assetType=GATE_SYSTEM`);
        }
        this.ref.close(true);
      },
      error: () => {
        this.requestStarted.set(false);
      },
    };
  }

  /**
   * Checks the file type of the given URL.
   * @param url The URL of the file to check.
   * @returns "pdf" if the URL is a PDF, "image" if it is an image, and "other" otherwise.
   */
  private getFileType(url: string): string {
    if (PDF_PATTERN.test(url)) {
      return "pdf";
    } else if (IMAGE_PATTERN.test(url)) {
      return "image";
    } else {
      return "other";
    }
  }

  /**
   * Loads a PDF as an image.
   * @param pdfUrl The URL of the PDF to load.
   * @returns A data URL of the PDF as an image, or null if an error occurs.
   */
  private async loadPdfAsImage(pdfUrl: string) {
    try {
      GlobalWorkerOptions.workerSrc = "assets/pdf.worker.min.mjs";

      const loadingTask = getDocument({ url: pdfUrl });
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1); // Get the first page

      const scale = 2; // Adjust for better resolution
      const viewport = page.getViewport({ scale });

      // Create a canvas element
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d")!;
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const renderContext: RenderParameters = {
        canvas: canvas,
        canvasContext: context,
        viewport: viewport,
      };

      await page.render(renderContext).promise;

      // Convert canvas to image URL
      return canvas.toDataURL("image/png");
    } catch (error) {
      console.error("Error loading PDF:", error);
      return;
    }
  }

  /**
   * Extracts the floor map source from the gate system's building.
   * If the floor map source is a PDF, it is loaded as an image.
   * @param gateSystem The gate system to extract the floor map source from.
   */
  private extractFloorMapSource(gateSystem: GateSystem) {
    if (gateSystem.building?.buildingFloorMapUrl) {
      this.floorMapSource = gateSystem.building?.buildingFloorMapUrl;
      const fileType = this.getFileType(gateSystem.building?.buildingFloorMapUrl);
      switch (fileType) {
        case "image":
          this.floorMapSource = gateSystem.building?.buildingFloorMapUrl;
          break;
        case "pdf":
          this.loadPdfAsImage(gateSystem.building?.buildingFloorMapUrl).then(
            (image) => (this.floorMapSource = image ?? "")
          );
          break;
        default:
          break;
      }
    }
  }

  /**
   * Creates or edits a gate system based on the component's action type and gate system data.
   * Subscribes to the result of the server communication and handles the response in afterServerCommunication.
   */
  private saveGateSystem() {
    this.requestStarted.set(true);

    if (this.actionType === "add") {
      const directParent = this.gateSystem.directParentId;
      this.assetService
        .createAsset(ASSET_TYPES.GATE_SYSTEM, this.gateSystem, directParent)
        .pipe(first())
        .subscribe(this.afterServerCommunication());
    } else if (this.actionType === "edit") {
      this.assetService
        .editAsset(ASSET_TYPES.GATE_SYSTEM, this.gateSystem)
        .pipe(first())
        .subscribe(this.afterServerCommunication("edit", this.gateSystem.id));
    }
  }

  /**
   * Retrieves all available cores from the Botevo Core service.
   * @returns Observable of core objects.
   */
  private getCores() {
    return this.coreService.getCores().pipe(first());
  }

  /**
   * Retrieves all available gate types from the Gate Catalog service.
   * @returns Observable of gate type objects.
   */
  private getGateTypesOptions() {
    return this.gateCatalog.getGateTypes().pipe(first());
  }

  /**
   * Retrieves all available controller manufacturers from the controller catalog.
   * @returns An observable of an array of manufacturer objects.
   * The array is empty if there are no manufacturers available.
   */
  private getControllerManufacturers(): Observable<ControllerManufacturerObject[]> {
    return this.controllerCatalog.getCatalogItems(CONTROLLER_CATALOG_ITEM_TYPES.MANUFACTURER).pipe(
      map((items) => {
        if (items) {
          return items.flatMap((manufacturer: ControllerCatalogItem) => {
            if (manufacturer) {
              return manufacturer.values as ControllerManufacturerObject;
            } else {
              return [];
            }
          });
        } else {
          return [];
        }
      }, first())
    );
  }
}
